import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Empty } from 'antd';
import { useAuth } from '../../../../providers/AuthProvider';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import DashboardCard, { LastUpdatedPill } from './DashboardCard';

/**
 * Teams card for the new talent dashboard.
 * Functional / Tailwind port of TeamsPanel.
 */

const daysSince = (date) => {
    if (!date || !TB.moment(date).isValid()) return null;
    return Math.max(0, TB.moment().diff(TB.moment(date), 'days'));
};

const getInitials = (name = '') => String(name)
    .split(/\s+/)
    .filter(word => /[a-z0-9]/i.test(word))
    .slice(0, 2)
    .map(word => word.replace(/[^a-z0-9]/gi, '')[0])
    .join('')
    .toUpperCase();

const TeamAvatar = ({ team }) => (
    team.banner_image_path ? (
        <img
            src={team.banner_image_path}
            alt=""
            className="h-9 w-9 shrink-0 rounded-full object-cover"
        />
    ) : (
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary" aria-hidden="true">
            {getInitials(team.name) || <i className="fas fa-users text-xs" />}
        </span>
    )
);

const TeamItem = ({ team, teamType }) => {
    const members = parseInt(team.positions_count) || 0;

    return (
        <Link
            to={team.is_invite ? `/${teamType}/teams/invited` : `/${teamType}/team/manage/${team.id}`}
            className="flex items-center gap-3 rounded-xl border border-solid border-gray-200 bg-white p-3 text-gray-900 hover:border-primary/30 hover:bg-primary/5 hover:text-gray-900 hover:no-underline"
        >
            <TeamAvatar team={team} />

            <span className="min-w-0 flex-1 truncate text-sm font-semibold" title={team.name}>
                {team.name}
            </span>

            {team.is_invite ? (
                <span className="shrink-0 rounded-md bg-green-50 px-2 py-0.5 text-xxs font-bold uppercase tracking-wider text-green-700">
                    New invite
                </span>
            ) : (
                <span className="shrink-0 whitespace-nowrap text-xxs text-gray-500">
                    {members} member{members === 1 ? '' : 's'}
                </span>
            )}
        </Link>
    );
};

const TeamsCard = ({
    teams,
    isLoading,
    error,
    refetch,
    totalUpdates,
    lastViewed,
    title = 'Teams',
    className,
    disabled,
    disabledReason = 'Your profile must be complete and your submission accepted in create/manage teams.',
}) => {
    const { auth } = useAuth();

    const teamType = auth?.canManageClientResourceTalent?.() ? 'supplier' : 'talent';

    const isDisabled = (() => {
        if (disabled) return true;
        if (auth?.canManageClientResourceTalent?.() || auth?.isTalentSupplierResource?.()) return false;
        return !auth?.isProfileApproved?.();
    })();

    const hasTeams = !isLoading && !error && teams?.length > 0;

    // Same ordering as TeamsPanel: orderBy is_invite asc (existing teams first, invites after)
    const orderedTeams = hasTeams ? TB.orderBy(teams, ['is_invite'], ['asc']) : [];

    const renderBody = () => {
        if (isLoading) {
            return (
                <div className="flex min-h-[160px] items-center justify-center">
                    <LoadingSpinner size="lg" />
                </div>
            );
        }

        if (error) {
            return (
                <div className="flex min-h-[160px] flex-col items-center justify-center gap-3 text-center">
                    <i className="fa fa-exclamation-circle text-2xl text-gray-300" aria-hidden="true" />
                    <p className="m-0 text-sm text-gray-500">{TB.trans('api.server_error.title')}</p>
                    {TB.isFunction(refetch) && (
                        <button
                            type="button"
                            onClick={() => refetch()}
                            className="inline-flex items-center gap-2 rounded-lg border border-solid border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:border-primary hover:text-primary"
                        >
                            <i className="fa fa-redo-alt text-xs" aria-hidden="true" />
                            Retry
                        </button>
                    )}
                </div>
            );
        }

        if (!teams) return null;

        if (!hasTeams) {
            return (
                <Empty
                    className="m-0 flex flex-col items-center justify-center py-4"
                    image="/images/heads.png"
                    imageStyle={{ height: 60 }}
                    description={(
                        <span className="flex flex-col items-center gap-1">
                            <span className="text-base font-semibold text-gray-900">No Teams Yet</span>
                            <span className="text-sm text-gray-500">
                                Form teams to deliver projects with people you already know and love working with.
                            </span>
                        </span>
                    )}
                >
                    {!auth?.isTalentSupplierResource?.() && (
                        <Link
                            to={`/${teamType}/team/setup`}
                            className="inline-flex items-center justify-center whitespace-nowrap rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-dark hover:text-white hover:no-underline"
                        >
                            Create New Team
                        </Link>
                    )}
                </Empty>
            );
        }

        return (
            <div className="-mr-1 flex max-h-[325px] flex-col gap-3 overflow-y-auto pr-1">
                {orderedTeams.map(team => (
                    <TeamItem key={team.id} team={team} teamType={teamType} />
                ))}
            </div>
        );
    };

    return (
        <DashboardCard
            title={title}
            count={totalUpdates}
            className={className}
            disabled={isDisabled}
            disabledReason={disabledReason}
            headerRight={(
                <div className="flex items-center gap-3">
                    <LastUpdatedPill prefix="" days={daysSince(lastViewed)} />
                    {hasTeams && (
                        <Link
                            to={`/${teamType}/teams/all`}
                            className="whitespace-nowrap text-sm font-semibold text-primary hover:text-primary-dark hover:no-underline"
                        >
                            View teams
                        </Link>
                    )}
                </div>
            )}
        >
            {renderBody()}
        </DashboardCard>
    );
};

TeamsCard.propTypes = {
    teams: PropTypes.array,
    isLoading: PropTypes.bool,
    error: PropTypes.any,
    refetch: PropTypes.func,
    totalUpdates: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    lastViewed: PropTypes.any,
    title: PropTypes.node,
    className: PropTypes.string,
    disabled: PropTypes.any,
    disabledReason: PropTypes.node,
};

export default TeamsCard;
